import { DatePipe, NgClass } from '@angular/common';
import { ChangeDetectionStrategy, ChangeDetectorRef, Component, DestroyRef, OnInit, ViewChild, ViewEncapsulation, inject } from '@angular/core';
import { takeUntilDestroyed } from '@angular/core/rxjs-interop';
import { MatBadgeModule } from '@angular/material/badge';
import { MatButtonModule } from '@angular/material/button';
import { MatDividerModule } from '@angular/material/divider';
import { MatIconModule } from '@angular/material/icon';
import { MatMenuModule, MatMenuTrigger } from '@angular/material/menu';
import { MatTooltipModule } from '@angular/material/tooltip';
import { RouterLink } from '@angular/router';
import { AuthService } from 'app/modules/auth/auth.service';
import { NotificationsService } from 'app/modules/notifications/notifications.service';

@Component({
  selector: 'app-notifications',
  standalone: true,
  imports: [MatButtonModule, MatMenuModule, MatIconModule, MatBadgeModule, MatDividerModule, MatTooltipModule, NgClass, DatePipe, RouterLink],
  templateUrl: './notifications.component.html',
  styleUrl: './notifications.component.scss',
  encapsulation: ViewEncapsulation.None,
  changeDetection: ChangeDetectionStrategy.Default,
})
export class NotificationsComponent implements OnInit {
  private _notifications = inject(NotificationsService);
  private _auth = inject(AuthService);
  private _cdr = inject(ChangeDetectorRef);
  private _destroyRef = inject(DestroyRef);

  @ViewChild(MatMenuTrigger) private _menuTrigger: MatMenuTrigger;

  notificationList: any[] = [];
  readonly unreadNotifications = this._notifications.unreadCount;

  private profile: any;

  ngOnInit(): void {
    this.profile = this._auth.profile();
    this.getNotifications();
  }

  public getNotifications() {
    const filter = {
      where: [{ column: 'user_id', value: this.profile.id }],
      orderBy: { created_at: 'desc' },
    };

    this._notifications.getList(filter, 1, 5).subscribe({
      next: (data: any) => {
        if (data.status === 'success') {
          this.notificationList = (data.record.data || []).map((item: any) => Object.assign(item, this._notifications.link(item, this.profile?.company?.type)));
          this._cdr.markForCheck();
        }
      },
    });

    this._notifications.refreshUnreadCount();
  }

  public markAllNotifIsRead(event: any) {
    event.stopPropagation();
    this.notificationList.forEach((element) => {
      element.is_read = 1;
    });
    this._notifications.clearUnreadCount();
    this._notifications.markAllIsRead().subscribe();
  }

  public markNotifIsRead(item: any, event: any) {
    event.stopPropagation();
    if (item.is_read) return;
    this.setIsRead(item, true);
  }

  public toggleNotifIsRead(item: any, event: Event) {
    event.stopPropagation();
    this.setIsRead(item, !item.is_read);
  }

  public openNotification(item: any, event: MouseEvent) {
    if (item.is_read < 1) {
      this.markNotifIsRead(item, event);
    }
    if (event.button === 0 && !event.ctrlKey && !event.metaKey && !event.shiftKey && !event.altKey) {
      this._menuTrigger?.closeMenu();
    }
  }

  private setIsRead(item: any, isRead: boolean): void {
    const previousState = item.is_read;

    item.is_read = isRead ? 1 : 0;
    this._notifications.adjustUnreadCount(isRead ? -1 : 1);
    this._cdr.markForCheck();

    this._notifications
      .setIsRead(item.id, isRead)
      .pipe(takeUntilDestroyed(this._destroyRef))
      .subscribe({
        error: () => {
          item.is_read = previousState;
          this._notifications.adjustUnreadCount(isRead ? 1 : -1);
          this._cdr.markForCheck();
        },
      });
  }
}
